import { BUILD_METADATA } from './buildMetadata';
import type { ApplicationInsightsEvent } from '../services/analytics';

interface NetworkInformationLike {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

export interface EventGroupTelemetryCounters {
  formStartedAt: number;
  requestStartedAt: number;
  searchCount: number;
  addCount: number;
  removeCount: number;
  partnerCount: number;
  companyCount: number;
}

function safeReferrer(): string | undefined {
  if (!document.referrer) return undefined;
  try {
    const url = new URL(document.referrer);
    return `${url.host}${url.pathname}`.slice(0, 500);
  } catch {
    return undefined;
  }
}

export function collectEventGroupClientTelemetry(
  requestId: string,
  outcome: string,
  counters: EventGroupTelemetryCounters,
): ApplicationInsightsEvent {
  const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
  const params = new URLSearchParams(window.location.search);
  const navigation = typeof performance.getEntriesByType === 'function'
    ? performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    : undefined;
  const now = typeof performance.now === 'function' ? performance.now() : Date.now();
  const prefersDark = typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : undefined;
  const prefersReducedMotion = typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : undefined;
  return {
    name: 'EventGroupCreateClient',
    properties: {
      schemaVersion: '1',
      requestId,
      outcome,
      locale: navigator.language,
      languages: (navigator.languages || []).slice(0, 10).join(','),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      colorScheme: prefersDark == null ? undefined : prefersDark ? 'dark' : 'light',
      reducedMotion: prefersReducedMotion,
      effectiveNetworkType: connection?.effectiveType,
      saveData: connection?.saveData,
      navigationType: navigation?.type,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      referrer: safeReferrer(),
      utmSource: params.get('utm_source')?.slice(0, 100),
      utmMedium: params.get('utm_medium')?.slice(0, 100),
      utmCampaign: params.get('utm_campaign')?.slice(0, 100),
      appRelease: BUILD_METADATA.commitHashShort,
      privacyNoticeVersion: '2026-08-22',
    },
    measurements: {
      apiLatencyMs: Math.max(0, now - counters.requestStartedAt),
      formDurationMs: Math.max(0, now - counters.formStartedAt),
      searchCount: counters.searchCount,
      addCount: counters.addCount,
      removeCount: counters.removeCount,
      selectedEntityCount: counters.partnerCount + counters.companyCount,
      partnerCount: counters.partnerCount,
      companyCount: counters.companyCount,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      devicePixelRatio: window.devicePixelRatio,
      downlinkMbps: connection?.downlink,
      networkRttMs: connection?.rtt,
      maxTouchPoints: navigator.maxTouchPoints,
    },
  };
}